import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { WindowsLogo, GlobeHemisphereWest, CheckCircle, WarningCircle, CircleNotch, Eye, Copy, ArrowRight, X } from '@phosphor-icons/react';

const seriesData = {
  Macro: ['macroj', 'macrog', 'macrox'],
  True: ['truea', 'trueb', 'truex', 'truez', 'truev'],
  Jack: ['jacka', 'jackx'],
  False: ['falsex', 'falsez', 'falsem'],
  Mobi: ['mobi', 'mobi2', 'applesos', 'mobi3', 'sara', 'siera']
};

const AzureNewDeployment = () => {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ account_id: '' });
  const [prefixCounts, setPrefixCounts] = useState({});
  const [deployments, setDeployments] = useState([]);
  const [deploying, setDeploying] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const res = await axios.get(`/api/azure/accounts`, { withCredentials: true });
      setAccounts(res.data);
      if (res.data.length > 0) setForm(f => ({...f, account_id: res.data[0].id}));
    } catch (e) {
      toast.error("Failed to load setup data");
    }
  };

  const togglePrefix = (prefix) => {
    setPrefixCounts(prev => {
      const next = { ...prev };
      if (next[prefix]) {
        delete next[prefix];
      } else {
        next[prefix] = 1;
      }
      return next;
    });
  };

  const toggleSeries = (prefixes) => {
    const allSelected = prefixes.every(p => prefixCounts[p]);
    setPrefixCounts(prev => {
      const next = { ...prev };
      if (allSelected) {
        prefixes.forEach(p => delete next[p]);
      } else {
        prefixes.forEach(p => { if (!next[p]) next[p] = 1; });
      }
      return next;
    });
  };

  const updatePrefixCount = (prefix, count) => {
    if (count < 1) count = 1;
    setPrefixCounts(prev => ({ ...prev, [prefix]: count }));
  };

  const handleDeploy = async () => {
    if (!form.account_id) return toast.error("Select an Azure account.");
    
    const items = Object.entries(prefixCounts).map(([prefix, count]) => ({ prefix, count }));
    if (items.length === 0) return toast.error("Select at least one URL prefix.");
    
    setDeploying(true);
    setShowCompletionModal(false);
    
    try {
      const res = await axios.post(`/api/azure/deployments`, {
        account_id: form.account_id,
        items
      }, { withCredentials: true });

      const initialDeps = res.data.map(d => ({ ...d, url: null, error: null }));
      setDeployments(initialDeps);

      initialDeps.forEach((dep, index) => {
        let errCount = 0;
        const poll = async () => {
          try {
            const pollRes = await axios.get(`/api/azure/deployments/${dep.id}`, { withCredentials: true });
            errCount = 0;
            setDeployments(prev => {
              const arr = [...prev];
              if (pollRes.data.status === 'live') {
                arr[index] = { ...arr[index], status: 'live', url: pollRes.data.public_url };
              } else if (pollRes.data.status === 'failed') {
                arr[index] = { ...arr[index], status: 'failed', error: pollRes.data.error_msg || "Deployment failed" };
              } else {
                arr[index] = { ...arr[index], status: pollRes.data.status };
              }
              return arr;
            });

            if (pollRes.data.status !== 'live' && pollRes.data.status !== 'failed') {
              setTimeout(poll, 15000); // Azure deployment takes a few minutes, poll slowly
            }
          } catch (err) {
            errCount += 1;
            if (errCount >= 5) {
              setDeployments(prev => {
                const arr = [...prev];
                arr[index] = { ...arr[index], status: 'failed', error: "Lost connection while checking status. Refresh to see the latest state." };
                return arr;
              });
            } else {
              setTimeout(poll, 15000); // transient error, keep trying
            }
          }
        };
        poll();
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to trigger Azure deployment");
      setDeploying(false);
    }
  };

  // Monitor deployment progress for Completion Modal
  useEffect(() => {
    if (deploying && deployments.length > 0) {
      const allDone = deployments.every(d => d.status === 'live' || d.status === 'failed');
      if (allDone) {
        setDeploying(false);
        setShowCompletionModal(true);
      }
    }
  }, [deployments, deploying]);

  const handleCopyAll = () => {
    const urls = deployments
      .filter(d => d.status === 'live' && d.url)
      .map(d => `${d.url}/index.html`)
      .join('\n');
    
    if (urls) {
      navigator.clipboard.writeText(urls);
      toast.success("All Live URLs copied to clipboard!");
    } else {
      toast.error("No Live URLs to copy.");
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'building': return <span className="text-[#0078D4] text-xs font-bold animate-pulse flex items-center gap-1"><CircleNotch className="animate-spin" /> Provisioning Azure Resources...</span>;
      case 'live': return <span className="text-[#39FF14] text-xs font-bold flex items-center gap-1"><CheckCircle /> Live via CDN</span>;
      case 'failed': return <span className="text-red-500 text-xs font-bold flex items-center gap-1"><WarningCircle /> Failed</span>;
      default: return null;
    }
  };

  const totalRequested = Object.values(prefixCounts).reduce((a, b) => a + b, 0);
  const selectedAccount = accounts.find(a => a.id === form.account_id);
  const overCapacity = !!(selectedAccount && typeof selectedAccount.remaining_urls === 'number' && totalRequested > selectedAccount.remaining_urls);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500 relative">
      <div>
        <h1 className="text-4xl font-black text-[#0078D4] mb-2">AZURE NEW DEPLOYMENT</h1>
        <p className="text-slate-400 font-mono">Provision Storage Accounts and CDN endpoints via prefixes.</p>
      </div>
      
      <div className="bg-card border border-white/10 p-8 max-w-4xl space-y-8">
        
        {/* URL Prefix Selector */}
        <div className="space-y-4">
          <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block">1. Select URL Prefixes</label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(seriesData).map(([series, prefixes]) => {
              const allSelected = prefixes.every(p => !!prefixCounts[p]);
              const someSelected = prefixes.some(p => !!prefixCounts[p]);
              return (
              <div key={series} className="border border-white/10 p-4 bg-[#0A0B0E]">
                <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-2">
                  <h3 className="text-white font-bold">{series} Series</h3>
                  <button
                    type="button"
                    onClick={() => toggleSeries(prefixes)}
                    data-testid={`azure-series-select-all-${series}`}
                    title={allSelected ? `Deselect all in ${series}` : `Select all in ${series}`}
                    className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-400 hover:text-white transition-colors group"
                  >
                    <span className={`w-5 h-5 border flex items-center justify-center transition-colors ${allSelected ? 'bg-[#0078D4] border-[#0078D4]' : someSelected ? 'bg-[#0078D4]/30 border-[#0078D4]' : 'border-white/20 group-hover:border-[#0078D4]'}`}>
                      {allSelected ? <CheckCircle size={16} weight="bold" className="text-white" /> : someSelected ? <span className="w-2.5 h-0.5 bg-white" /> : null}
                    </span>
                    All
                  </button>
                </div>
                <div className="space-y-2">
                  {prefixes.map(p => {
                    const isSelected = !!prefixCounts[p];
                    return (
                      <div key={p} className="flex items-center justify-between group">
                        <label className="flex items-center gap-3 cursor-pointer flex-1" onClick={() => togglePrefix(p)}>
                          <div className={`w-5 h-5 border flex items-center justify-center transition-colors ${isSelected ? 'bg-[#0078D4] border-[#0078D4]' : 'border-white/20 group-hover:border-[#0078D4]'}`}>
                            {isSelected && <CheckCircle size={16} weight="bold" className="text-white" />}
                          </div>
                          <span className="font-mono text-sm text-slate-300 group-hover:text-white transition-colors">{p}</span>
                        </label>
                        {isSelected && (
                          <input 
                            type="number" 
                            min="1" 
                            max="50"
                            value={prefixCounts[p]}
                            onChange={(e) => updatePrefixCount(p, parseInt(e.target.value) || 1)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-16 bg-[#13151A] border border-white/20 p-1 text-center font-mono text-sm focus:outline-none focus:border-[#0078D4] ml-2"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">2. Azure Account</label>
            <select 
              value={form.account_id} 
              onChange={(e) => setForm({...form, account_id: e.target.value})}
              className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#0078D4] appearance-none rounded-none"
            >
              <option value="">-- Choose an account --</option>
              {accounts.map(a => {
                const cap = (typeof a.remaining_urls === 'number') ? ` — ${a.used_urls}/${a.max_urls} used` : '';
                return <option key={a.id} value={a.id}>{a.label}{cap}</option>;
              })}
            </select>
            <p className="text-xs text-slate-500 mt-2">
              Total pending deployments: <strong className="text-white">{totalRequested}</strong>
            </p>
            {selectedAccount && typeof selectedAccount.remaining_urls === 'number' && (
              <div data-testid="azure-capacity-info" className={`mt-3 text-xs font-mono border p-3 ${overCapacity ? 'border-red-500/50 bg-red-500/10 text-red-400' : 'border-white/10 bg-[#0A0B0E] text-slate-400'}`}>
                Capacity: <strong className="text-white">{selectedAccount.used_urls}/{selectedAccount.max_urls}</strong> URLs live
                {selectedAccount.azure_tier ? ` (${selectedAccount.azure_tier} tier)` : ''} ·{' '}
                <strong className={selectedAccount.remaining_urls > 0 ? 'text-[#39FF14]' : 'text-red-400'}>{Math.max(0, selectedAccount.remaining_urls)} free</strong>
                {overCapacity && (
                  <div className="mt-1 text-red-400">
                    You requested {totalRequested} but only {Math.max(0, selectedAccount.remaining_urls)} slot(s) are free. Delete existing URLs or pick another account.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <button 
          onClick={handleDeploy}
          disabled={deploying || !form.account_id || Object.keys(prefixCounts).length === 0 || overCapacity}
          data-testid="azure-deploy-btn"
          className="w-full bg-[#0078D4] text-white px-8 py-4 font-black uppercase tracking-wider hover:bg-[#005a9e] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          DEPLOY TO AZURE <ArrowRight size={20} />
        </button>
      </div>

      {deployments.length > 0 && (
        <div className="bg-card border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10 bg-black/20 flex justify-between items-center">
            <div>
              <h3 className="font-bold uppercase tracking-wider text-[#0078D4]">Deployment Results</h3>
              <p className="text-xs text-slate-500 mt-1">Note: Azure CDN propagation can take 5-15 minutes after deployment completes.</p>
            </div>
            {!deploying && (
              <button 
                onClick={handleCopyAll}
                className="bg-[#0A0B0E] border border-white/20 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white/10 transition-colors flex items-center gap-2"
              >
                <Copy size={16} /> Copy All
              </button>
            )}
          </div>
          <table className="w-full text-left text-sm border-collapse">
            <tbody>
              {deployments.map((dep, i) => (
                <tr key={i} className="border-b border-white/10 hover:bg-white/5">
                  <td className="p-4 font-mono font-bold w-1/4">
                    <GlobeHemisphereWest className="inline mr-2 text-slate-500" />
                    {dep.site_name}
                  </td>
                  <td className="p-4 w-1/4">{getStatusDisplay(dep.status)}</td>
                  <td className="p-4 font-mono">
                    {dep.status === 'live' && dep.url && (
                      <div className="flex items-center gap-2">
                        <div className="bg-[#0A0B0E] border border-white/10 px-2 py-1 text-xs text-slate-400 font-mono truncate w-64 select-all" title={dep.url + '/index.html'}>
                          {dep.url}/index.html
                        </div>
                        <button onClick={() => { navigator.clipboard.writeText(dep.url + '/index.html'); toast.success("Copied!"); }} className="text-slate-400 hover:text-white transition-colors p-1"><Copy size={16} /></button>
                        <a href={dep.url + '/index.html'} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#0078D4] transition-colors p-1"><Eye size={16} /></a>
                      </div>
                    )}
                    {dep.status === 'failed' && <span className="text-red-500 text-xs">{dep.error}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Completion Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-[#0078D4]/50 p-8 w-full max-w-2xl relative animate-in zoom-in-95 duration-200 shadow-[0_0_20px_rgba(0,120,212,0.2)]">
            <button onClick={() => setShowCompletionModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold mb-2 text-[#0078D4]">DEPLOYMENT BATCH COMPLETE</h2>
            <p className="text-slate-400 mb-6">Your requested URLs have finished provisioning.</p>
            
            <div className="bg-[#0A0B0E] border border-white/10 p-4 max-h-[300px] overflow-y-auto mb-6 font-mono text-sm space-y-1">
              {deployments.map(dep => (
                <div key={dep.id} className="flex items-center gap-2">
                  {dep.status === 'live' ? (
                    <span className="text-[#39FF14]">{dep.url}/index.html</span>
                  ) : (
                    <span className="text-red-500 line-through">{dep.site_name} (Failed)</span>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <button 
                onClick={handleCopyAll}
                className="flex-1 bg-[#0078D4] text-white py-3 font-bold uppercase tracking-wider hover:bg-[#005a9e] transition-colors flex items-center justify-center gap-2"
              >
                <Copy size={20} /> Copy Live URLs
              </button>
              <button 
                onClick={() => setShowCompletionModal(false)}
                className="flex-1 bg-transparent border border-white/20 text-white py-3 font-bold uppercase tracking-wider hover:bg-white/10 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AzureNewDeployment;